import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { supabaseClient } from '../../config/supabase-client';
import { PasswordWrapper, mediumPasswordTest } from '../Password/Password';
import { ConfirmDialog } from 'primereact/confirmdialog';
import '../Auth/authStuff.css';
import { useAuth } from '../../context/AuthProvider';
import { useToast } from '../../context/ToastProvider';

const PasswordResetPage = () => {

  const [password, setPassword] = useState('');
  const [isDialog, setIsDialog] = useState(false);
  const [passwordRepeat, setPasswordRepeat] = useState('');
  const [isMatch, setIsMatch] = useState(true);
  const navigate = useNavigate();
  const isRecoveryCompleteRef = useRef(false); // useRef persists between remounts, unlike useState

  const { isRecoveryEvent, signOut } = useAuth();

  useEffect(() => {

    if (!isRecoveryEvent) {
      navigate('/'); // redirect to home
    }

    return () => {
      // React.StrictMode renders components twice which would trigger this unmount
      // so we add a check
      if (isRecoveryCompleteRef.current) {
        signOut();
      }
    };
  }, [isRecoveryEvent, navigate, signOut]);

  const {showSuccess, showError } = useToast();

  const handleToastError = (error: string) => {
    showError({ detail: error, summary: 'Could not update password' });
  }

  const handleToastSuccess = (success: string) : void => {
    showSuccess({detail: success});
  }

  const accept = async () => {
    try {
      if (password !== passwordRepeat) {
        throw Error('Passwords do not match.');
      }

      const { error } = await supabaseClient.auth.updateUser({ password: password })

      if (error) throw error;

      isRecoveryCompleteRef.current = true;
      handleToastSuccess('Your password has been updated.');
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (error) {
      handleToastError(error.message);
    }
  }

  const reject = () => {
    setIsDialog(false);
  }

  const handlePasswordReset = async () => {
    try {
      if (password !== passwordRepeat) {
        setIsMatch(false);
        throw Error('Passwords do not match.');
      }
      if (mediumPasswordTest.test(password) === false) {
        setIsMatch(false);
        throw Error('Password must be at least 8 characters and contain letters.')
      }
      setIsMatch(true);
      setIsDialog(true);
    } catch (error) {
      handleToastError(error.message);
    }
  };

  return (
    <div className='flex-column h-screen flex align-items-center justify-content-center pb-8'>
      <ConfirmDialog
        visible={isDialog}
        header='Are you sure?'
        onHide={() => setIsDialog(false)}
        accept={accept}
        reject={reject}
        resizable={false}
        draggable={false}
        data-testid='confirm-submit-dialog'
      >
      </ConfirmDialog>
      <div className="">
        <h1 className='pb-4'>Reset Your Password</h1>
        <div className="password-text-container">
          <span className="p-float-label mr-2">
            <PasswordWrapper
              id='password'
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              toggleMask
              data-testid="password-input"
            />
            <label htmlFor="password" >New Password</label>
          </span>
          <span className="p-float-label" >
            <PasswordWrapper
              id='password'
              value={passwordRepeat}
              onChange={(e) => setPasswordRepeat(e.target.value)}
              toggleMask
              feedback={false}
              invalid={!isMatch}
              data-testid="password-input-repeat"
            />
            <label htmlFor="password" >Repeat Password</label>
          </span>
        </div>
        <Button label="Update Password" onClick={handlePasswordReset} data-testid='update-button' />
      </div>
    </div>
  );
};

export default PasswordResetPage;
